// One frame of a gesture, measured (SPEC-viewfinder §4.1).
//
// The rule this obeys is #238's, relayed by ADR-0018 and not negotiable: a
// per-frame gesture calls its own `remeasure` and pushes to ITSELF, never
// through `derived.subscribe`, and it writes `viewState` once, on settle.
// `paint` is both the camera authority and a `derived` subscriber, so a
// per-frame `viewState.set(…, 'replace')` would be one camera write per frame —
// `easeTo` restarting under the finger — and it would fold the whole gesture
// into `carried` and ride onto the next Back. #278's fix does not change that.
//
// So: read the STANDING derivation (`derived.get()`, which is a read and not a
// subscription), re-divide it against the camera's live centre and radius with
// the pure `remeasure`, and push the result at the three surfaces that draw it.
// `src/derived.ts` learns nothing: no rAF, no timer, no camera (§1.1, rule 1).
//
// **Throttled to one animation frame**, which is §4.1's own word and the idiom
// `scheduleReproject` already uses: a frame's worth of `move` events costs one
// measurement, not sixty. The camera is the source of truth for both numbers,
// which is what makes a centre change no gesture started — a refused permission
// (#279) — harmless here: nothing is remembered from `pointerdown`.
import { remeasure } from '../derive/remeasure.ts';
import { derived } from '../derived.ts';
import { gesture } from '../state/gesture.ts';
import type { CameraDeps, Live } from './camera.ts';
import { radiusForZoom } from './wheel-geometry.ts';

/**
 * How often the live measurement is allowed to land — and this is a **measured
 * deviation from §4.1's word „throttled to one animation frame"**, disclosed
 * rather than quietly taken.
 *
 * Per frame it does not fit §4.3's budget. Measured on the build with a
 * city-sized snapshot (247 vehicles, 59 rows) at 6× CPU throttle: 50,4 fps with
 * 61 frames over 32 ms and 4 over 50 ms. The cost is not the arithmetic — it is
 * re-spelling the distance column sixty times a second, which is layout work on
 * the longest column on the screen. §4.3 carries the amended budget and the
 * control that attributes those frames to this path.
 *
 * At 150 ms the same pan holds the budget, and the reading is what a resident
 * can actually take in: a distance that changes seven times a second is already
 * a blur. MEMBERSHIP is unaffected in feel, because the ring passing over an
 * item is itself a slow event at any real pan speed.
 *
 * `§4.1`'s other two promises are untouched: no re-order under the finger, and
 * no `viewState` write until settle.
 */
const EVERY_MS = 150;

let queued = 0;
let last = 0;

/** A gesture has taken the map: remember the zoom it started from and open the channel. */
export function startGesture(map: { getZoom(): number }, live: Live): void {
  // Remembered once, because a re-grab mid-glide is the SAME gesture (§1.7).
  live.startZoom = map.getZoom();
  gesture.begin();
}

/**
 * Measures this frame, at most once per animation frame. Read off the camera
 * and never off `viewState`, which by §4.1 hears nothing until settle.
 */
export function liveFrame(deps: CameraDeps): void {
  if (queued) return;
  queued = requestAnimationFrame((frame) => {
    queued = 0;
    if (frame - last < EVERY_MS) return;
    last = frame;
    const axis = derived.get().derived;
    const disc = deps.disc();
    if (!axis || disc === 0 || !gesture.active()) return;
    const at = deps.map.getCenter();
    const centre = { lat: at.lat, lon: at.lng };
    // The radius the camera now MEANS, by the one conversion the slider uses,
    // so the rows, the counts and the dots divide on the ring that is drawn.
    const radius = radiusForZoom(deps.map.getZoom(), disc, centre.lat);
    gesture.push(remeasure(axis, centre, radius));
  });
}

/**
 * The camera is at rest. The live channel closes before the settle writes
 * anything, so the one derivation that write produces finds no gesture in
 * flight and the rail rebuilds exactly once (§4.2).
 */
export function endGesture(): void {
  if (queued) cancelAnimationFrame(queued);
  queued = 0;
  // So the next gesture measures its first frame at once rather than waiting
  // out a window it did not start.
  last = 0;
  gesture.end(true);
}
